import { login as apiLogin, getCurrentUser as apiGetUser, refreshAuthToken } from '@/lib/api/auth';
import { getAllCustomers as apiGetCustomers } from '@/lib/api/customers';
import { tokenManager } from './tokenManager';
import {
  setUserContext,
  setCustomerContext,
  enableSessionRecording,
  disableSessionRecording,
} from '@/lib/sentry';
import { logAuth, logCustomer } from '@/lib/breadcrumbs';
import type { User, Customer, ClientUser } from '@/contexts/auth/types';
import api from '@/lib/api/core';
import { toClientUser } from '@/contexts/auth/utils/userUtils';
import axios from 'axios';
import { logDebug } from '@/utils/debug';
import { captureException } from '@/lib/sentry';

/**
 * Authentication Service
 * 
 * Centralizes authentication logic while using the existing API endpoints.
 * This service integrates with the existing API utilities from lib/api/
 */

/**
 * Map the raw ``/crm/users/me/`` response onto our ``User`` type.
 *
 * ``last_active_customer`` is NOT a top-level field on the backend
 * ``UserMeSchema`` — it lives on the nested ``profile`` (a FK serialized as
 * its numeric id). Reading ``userResponse.last_active_customer`` directly
 * therefore always yields ``undefined``, which made the auth flow unable to
 * resolve the active customer and bounced every session to the
 * ``/select-customer`` picker. Fall back to ``profile.last_active_customer``
 * (stringified, since the rest of the app keys customers by string id).
 */
const mapUserResponse = (userResponse: any): User => {
  const profileLastActive = userResponse.profile?.last_active_customer;
  const lastActiveCustomer =
    userResponse.last_active_customer ??
    (profileLastActive != null ? String(profileLastActive) : undefined);

  return {
    id: userResponse.id,
    email: userResponse.email,
    first_name: userResponse.first_name || '',
    last_name: userResponse.last_name || '',
    is_superuser: userResponse.is_superuser,
    is_staff: userResponse.is_staff,
    // REQUIRED, not optional decoration: CustomerAdminGuard, the Topbar and
    // the mobile nav all decide whether someone administers the active
    // customer from these rows. Dropping them here made
    // ``administersCustomer`` permanently false, so every customer admin was
    // shown "Access Denied" on their own tenants and only Nodus staff got
    // through. The mapper listing fields one by one is what let a field the
    // API returns simply go missing.
    customer_access: userResponse.customer_access,
    last_active_customer: lastActiveCustomer,
    profile: userResponse.profile || { theme: 'light' },
  };
};

/**
 * Format error messages to be user-friendly
 */
const formatAuthError = (error: any): string => {
  // Handle Axios errors
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    
    // Common authentication errors
    if (status === 401) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    
    if (status === 403) {
      return 'You do not have permission to access this resource.';
    }
    
    if (status === 404) {
      return 'Authentication service not found. Please try again later.';
    }
    
    if (status === 500) {
      return 'Server error occurred. Please try again later or contact support.';
    }
    
    if (error.message === 'Network Error') {
      return 'Unable to connect to the authentication server. Please check your internet connection.';
    }
    
    // Use server message if available
    const serverMessage = error.response?.data?.detail || error.response?.data?.message;
    if (serverMessage) {
      return serverMessage;
    }
  }
  
  // For non-axios errors or if we can't extract a specific message
  return 'Authentication failed. Please check your credentials and try again.';
};

/**
 * Login a user with email and password
 */
export const login = async (email: string, password: string): Promise<{
  success: boolean;
  user?: User;
  error?: string;
}> => {
  try {
    // Clear any existing session data first to ensure a fresh start
    clearSession();
    
    // Use the existing login function from lib/api/auth.ts
    const tokenResponse = await apiLogin(email, password);
    // Note: The token is already saved to localStorage in the apiLogin function
    
    // With the token set, get the user data using the existing API function
    const userResponse = await apiGetUser();

    // Convert API response to our User type, matching the existing type structure
    const user: User = mapUserResponse(userResponse);

    // Store user in localStorage for session persistence - using ClientUser
    localStorage.setItem('user', JSON.stringify(toClientUser(user)));
    
    // Log successful login
    logAuth('Login successful', { email });
    
    // Set user context for analytics
    setUserContext(user);
    
    return {
      success: true,
      user
    };
  } catch (error) {
    // Log failed login attempt
    logAuth('Login failed', { email, error: String(error) });
    
    // Convert technical error to user-friendly message
    const formattedError = formatAuthError(error);
    
    return {
      success: false,
      error: formattedError
    };
  }
};

/**
 * Check if the user has a valid session
 */
/**
 * True only when the backend REJECTED the credentials (401/403). By the time
 * such an error reaches a caller, the response interceptor in
 * ``lib/api/core.ts`` has already tried a refresh, so it really means "this
 * session is over".
 *
 * Everything else — no response at all (dropped connection, DNS, CORS), a 5xx,
 * 408/429, or a bug in our own code — says nothing about the session and must
 * NOT log the user out.
 */
export const isAuthRejection = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === 401 || status === 403;
};

/**
 * True when a failure says "the server could not be reached or is struggling",
 * as opposed to a bug in our code: no response at all (dropped connection,
 * DNS, CORS-masked), a 5xx, 408, 429, or the gateway timeout that
 * ``lib/api/core.ts`` turns into ``Error('Request timed out')``.
 */
export const isTransientFailure = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === undefined) return true;
    return status >= 500 || status === 408 || status === 429;
  }
  return error instanceof Error && error.message === 'Request timed out';
};

/** Backoff before each retry of ``/crm/users/me/`` on a transient failure. */
const SESSION_CHECK_RETRY_DELAYS_MS = [500, 1500];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * ``GET /crm/users/me/``, retried on transient failures.
 *
 * This runs on EVERY full page load. It used to treat any failure as an
 * invalid session and wipe the tokens, so one dropped request logged the user
 * out for good (the CI smoke run, ~100 full loads against the shared dev
 * backend, hit exactly that). Only transient failures are retried.
 */
const getUserWithRetry = async (): Promise<Awaited<ReturnType<typeof apiGetUser>>> => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await apiGetUser({ quiet: true });
    } catch (error) {
      if (!isTransientFailure(error) || attempt >= SESSION_CHECK_RETRY_DELAYS_MS.length) throw error;
      await sleep(SESSION_CHECK_RETRY_DELAYS_MS[attempt]);
    }
  }
};

/** Short label for a failure, for breadcrumbs: ``502``, ``ERR_NETWORK``, ``timeout``. */
const describeFailure = (error: unknown): string => {
  if (axios.isAxiosError(error)) return error.response ? String(error.response.status) : (error.code ?? 'no-response');
  return error instanceof Error ? error.message : String(error);
};

export interface SessionCheck {
  isValid: boolean;
  user: User | null;
  activeCustomer: Customer | null;
  /**
   * True when the session could NOT be verified — the server was unreachable
   * or erroring — but nothing rejected it, so the tokens are still in place.
   * That is not "signed out": callers must not send the user to /login on it
   * (#433), they should check again.
   */
  unreachable: boolean;
  /** Why it was unreachable (``502``, ``ERR_NETWORK``, ``timeout``) — for breadcrumbs. */
  failure?: string;
}

const UNREACHABLE = (failure: string): SessionCheck => ({
  isValid: false, user: null, activeCustomer: null, unreachable: true, failure,
});

export const checkSession = async (): Promise<SessionCheck> => {
  try {
    // Check if tokens exist
    if (!tokenManager.hasTokens()) {
      return { isValid: false, user: null, activeCustomer: null, unreachable: false };
    }
    
    // Verify token is valid by making an API call
    const userResponse = await getUserWithRetry();

    // Convert API response to our User type
    const user: User = mapUserResponse(userResponse);

    // Store user in localStorage - using ClientUser to strip sensitive information
    localStorage.setItem('user', JSON.stringify(toClientUser(user)));
    
    // Get active customer from localStorage
    let activeCustomer: Customer | null = null;
    const storedCustomer = localStorage.getItem('active_customer');
    
    if (storedCustomer) {
      try {
        activeCustomer = JSON.parse(storedCustomer);
      } catch (e) {
        // Use the direct logDebug since we don't have the user object yet
        logDebug(null, 'AuthService', 'Error parsing active customer from localStorage:', e);
      }
    }
    
    // Set user context for analytics
    setUserContext(user);
    if (activeCustomer) {
      setCustomerContext(activeCustomer);
    }
    
    return {
      isValid: true,
      user,
      activeCustomer,
      unreachable: false,
    };
  } catch (error) {
    logDebug(null, 'AuthService', 'Session validation failed:', error);
    
    // Only an auth rejection ends the session.
    if (isAuthRejection(error)) {
      clearSession();
      return { isValid: false, user: null, activeCustomer: null, unreachable: false };
    }
    // The server could not be reached: unverified, not invalid. Tokens stay.
    if (isTransientFailure(error) && tokenManager.hasTokens()) {
      return UNREACHABLE(describeFailure(error));
    }
    // Anything else is a bug on our side, not a connection problem: report it
    // and fall back to the login screen (the tokens are kept, as before).
    captureException(error, { type: 'session_check_error' });
    return { isValid: false, user: null, activeCustomer: null, unreachable: false };
  }
};

/** Wall-clock budget for the start-up session check, refresh retries included. */
export const SESSION_CHECK_BUDGET_MS = 20_000;

/** Back-off between start-up re-checks of an unreachable session. */
export const SESSION_RECHECK_DELAYS_MS = [2000, 5000];

/**
 * ``checkSession`` that answers within ``budgetMs`` no matter what. A check
 * can otherwise run for minutes: with an expired access token and no network,
 * every ``/users/me/`` attempt first waits out ``refreshToken``'s own retries.
 * The check that misses the deadline keeps running in the background; if it
 * ends in a rejection, the interceptor still signs the user out.
 */
export const checkSessionWithin = (budgetMs: number): Promise<SessionCheck> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<SessionCheck>((resolve) => {
    timer = setTimeout(() => resolve(UNREACHABLE('timeout')), Math.max(0, budgetMs));
  });
  return Promise.race([checkSession(), deadline]).finally(() => clearTimeout(timer));
};

/**
 * Start-up check: ``checkSession``, re-run with back-off while the server is
 * unreachable, all within ``budgetMs`` of wall-clock time (#433). A session
 * that could not be VERIFIED is not a signed-out session; if it is still
 * ``unreachable`` at the end, the guards show a retry screen (which keeps
 * re-checking in the background) instead of the login form.
 */
export const checkSessionPatiently = async (
  budgetMs: number = SESSION_CHECK_BUDGET_MS,
  delaysMs: readonly number[] = SESSION_RECHECK_DELAYS_MS,
): Promise<SessionCheck> => {
  const start = Date.now();
  const remaining = () => budgetMs - (Date.now() - start);
  let session = await checkSessionWithin(remaining());
  for (const delay of delaysMs) {
    if (!session.unreachable || remaining() <= delay) break;
    await sleep(delay);
    session = await checkSessionWithin(remaining());
  }
  return session;
};

/**
 * Clear all session data
 */
export const clearSession = (): void => {
  // Clear user context in analytics
  setUserContext(null);
  setCustomerContext(null);
  
  // Remove tokens and user data
  tokenManager.removeTokens();
  localStorage.removeItem('user');
  localStorage.removeItem('customers');
  localStorage.removeItem('active_customer');
  localStorage.removeItem('active_site');
  localStorage.removeItem('recording_status');
  
  // Clear the Authorization header
  delete api.defaults.headers.common['Authorization'];
};

/**
 * Log out the current user
 */
export const logout = async (): Promise<void> => {
  try {
    // Call logout endpoint if needed
    // await api.post('/auth/logout/');
    
    // Clear all session data
    clearSession();
    
    // Log logout event
    logAuth('User logged out');
  } catch (error) {
    logDebug(null, 'AuthService', 'Error during logout:', error);
    // Ensure session is cleared even if API call fails
    clearSession();
  }
};

/**
 * Get the current logged-in user data
 */
export const getCurrentUser = async (): Promise<User> => {
  try {
    // Use the existing API function
    const userResponse = await apiGetUser();

    // Convert API response to our User type, matching the existing type structure
    const user: User = mapUserResponse(userResponse);

    return user;
  } catch (error) {
    logDebug(null, 'AuthService', 'Error getting current user:', error);
    throw error;
  }
};

/**
 * Get all available customers for the current user
 */
export const getCustomers = async (): Promise<Customer[]> => {
  try {
    // Use the existing API function
    return await apiGetCustomers();
  } catch (error) {
    logDebug(null, 'AuthService', 'Error getting customers:', error);
    throw error;
  }
};

/**
 * Set the active customer for the current user session
 */
export const setActiveCustomer = async (customerId: string): Promise<void> => {
  try {
    await api.put(`/crm/customers/active/${customerId}/`);
    
    // Log customer change
    logCustomer('Active customer changed', { customerId });
  } catch (error) {
    logDebug(null, 'AuthService', 'Error setting active customer:', error);
    throw error;
  }
};

/**
 * Select a customer and prepare the application state
 */
export const selectCustomer = async (
  customer: Customer,
  customers?: Customer[]
): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    // If customers array is provided, verify this customer exists in it
    if (customers?.length) {
      const validCustomer = customers.find(c => c.id === customer.id);
      
      if (!validCustomer) {
        logDebug(null, 'AuthService', `Customer ${customer.id} not found in available customers`);
        return { 
          success: false, 
          error: "Selected customer is not available for this user" 
        };
      }
    }
    
    // Set as active in API (updates server session + profile.last_active_customer)
    await setActiveCustomer(customer.id);

    // Re-issue the JWT pair with the new ``active_customer_id`` claim.
    // ``setActiveCustomer`` only updates the Django session + profile;
    // the existing JWT in localStorage still carries the OLD
    // ``active_customer_id`` claim, and ``CustomerRequired`` reads
    // that claim first — so without this refresh every subsequent
    // request comes back scoped to the previous customer's data and
    // the swap appears to "not take effect".
    const refreshTokenValue = localStorage.getItem('refresh_token');
    if (refreshTokenValue) {
      try {
        await refreshAuthToken(refreshTokenValue, { active_customer_id: Number(customer.id) });
      } catch (refreshError) {
        // Token-refresh failure shouldn't block the select itself —
        // the server session was already updated above. The next
        // hard-reload in the calling flow will recover the token.
        logDebug(null, 'AuthService', 'JWT rebind after customer select failed (continuing):', refreshError);
      }
    }

    // Store in local storage
    localStorage.setItem('active_customer', JSON.stringify(customer));

    // Set customer context for analytics
    setCustomerContext(customer);

    // Log customer selection
    logCustomer('Customer selected', {
      customerId: customer.id,
      customerName: customer.name
    });

    return { success: true };
  } catch (error) {
    logDebug(null, 'AuthService', 'Error selecting customer:', error);
    return { 
      success: false,
      error: `Failed to select customer: ${error}`
    };
  }
};

/**
 * Auto-select a customer based on last active or first available
 */
export const autoSelectCustomer = async (
  user: User | null, 
  customers: Customer[]
): Promise<{
  success: boolean;
  customer?: Customer;
  error?: string;
}> => {
  if (!user || !customers.length) {
    logDebug(null, 'AuthService', 'Cannot auto-select customer: missing user or customer data');
    return {
      success: false,
      error: 'Missing user or customer data'
    };
  }

  try {
    // Try to get the last active customer
    let selectedCustomer = null;
    
    if (user.last_active_customer) {
      selectedCustomer = customers.find(c => c.id === user.last_active_customer);
    }
    
    // If no last active customer or it wasn't found, select the first customer
    if (!selectedCustomer && customers.length > 0) {
      selectedCustomer = customers[0];
    }
    
    if (selectedCustomer) {
      // Select the customer
      const result = await selectCustomer(selectedCustomer, customers);
      
      if (result.success) {
        return {
          success: true,
          customer: selectedCustomer
        };
      }
      
      return {
        success: false,
        error: result.error
      };
    }
    
    return {
      success: false,
      error: 'No customer available to select'
    };
  } catch (error) {
    logDebug(user, 'AuthService', 'Error auto-selecting customer:', error);
    return {
      success: false,
      error: `Error auto-selecting customer: ${error}`
    };
  }
};

/**
 * Get the current session's recording status
 */
export const getRecordingStatus = async (): Promise<{ isRecording: boolean; isPaused: boolean }> => {
  try {
    const storedStatus = localStorage.getItem('recording_status');
    
    if (storedStatus) {
      return JSON.parse(storedStatus);
    }
    
    return { isRecording: false, isPaused: false };
  } catch (error) {
    logDebug(null, 'AuthService', 'Error getting recording status:', error);
    return { isRecording: false, isPaused: false };
  }
};

/**
 * Start a new session recording
 */
export const startRecording = async (): Promise<void> => {
  try {
    enableSessionRecording();

    // Store recording status
    localStorage.setItem('recording_status', JSON.stringify({
      isRecording: true,
      isPaused: false
    }));
  } catch (error) {
    logDebug(null, 'AuthService', 'Error starting recording:', error);
    throw error;
  }
};

/**
 * Pause the current session recording
 */
export const pauseRecording = async (): Promise<void> => {
  try {
    disableSessionRecording();

    // Update recording status
    localStorage.setItem('recording_status', JSON.stringify({
      isRecording: true,
      isPaused: true
    }));
  } catch (error) {
    logDebug(null, 'AuthService', 'Error pausing recording:', error);
    throw error;
  }
};

/**
 * Resume the current session recording
 */
export const resumeRecording = async (): Promise<void> => {
  try {
    enableSessionRecording();

    // Update recording status
    localStorage.setItem('recording_status', JSON.stringify({
      isRecording: true,
      isPaused: false
    }));
  } catch (error) {
    logDebug(null, 'AuthService', 'Error resuming recording:', error);
    throw error;
  }
};

/**
 * Stop the current session recording
 */
export const stopRecording = async (): Promise<void> => {
  try {
    disableSessionRecording();

    // Clear recording status
    localStorage.setItem('recording_status', JSON.stringify({
      isRecording: false,
      isPaused: false
    }));
  } catch (error) {
    logDebug(null, 'AuthService', 'Error stopping recording:', error);
    throw error;
  }
};

// Export auth service as a single object
export const authService = {
  login,
  logout,
  checkSession,
  checkSessionPatiently,
  checkSessionWithin,
  clearSession,
  getCurrentUser,
  getCustomers,
  setActiveCustomer,
  selectCustomer,
  autoSelectCustomer,
  getRecordingStatus,
  startRecording,
  pauseRecording,
  resumeRecording,
  stopRecording
};